<template>
	<div class="modal fade show" tabindex="-1" role="dialog" style="display: block; background: rgba(0,0,0,0.5);">
		<div class="modal-dialog modal-xl" role="document">
			<div class="modal-content">
				<div class="modal-header bg-secondary text-white">
					<h5 class="modal-title"><i class="far fa-file-excel me-2"></i>Importa Attività e Voci da Excel Offline</h5>
					<button type="button" class="btn-close btn-close-white" @click="close()"></button>
				</div>

				<div class="modal-body">

					<!-- STEP 1: Upload -->
					<div v-if="step == 1">
						<div class="mb-3">
							<label class="form-label">File Excel (.xlsx) *</label>
							<p class="text-muted small mb-2">
								<strong>Colonne richieste:</strong> IUD | ID_ATTIVITA | ID_VOCE | DESCRIZIONE | QTA | RIP | PREZZO_UNIT | TOTALE | DATA_MODIFICA<br>
								IUD: lascia vuoto per auto-detect, scrivi <strong>D</strong> per cancellare. ID = 0 per nuovi elementi.
							</p>
							<input type="file" accept=".xlsx,.xls" @change="onFileSelect" class="form-control" />
						</div>

						<div class="mb-3">
							<a href="#" @click.prevent="scaricaTemplate()" class="btn btn-sm btn-outline-success">
								<i class="far fa-file-excel me-1"></i> Scarica Template aggiornato
							</a>
						</div>

						<div v-if="message" class="alert" :class="messageClass">{{ message }}</div>

						<div class="modal-footer px-0 pb-0">
							<button type="button" class="btn btn-secondary" @click="close()">Annulla</button>
							<button type="button" class="btn btn-primary" @click="parseExcel()" :disabled="!selectedFile || loading">
								<span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
								Analizza
							</button>
						</div>
					</div>

					<!-- STEP 2: Preview + Conflitti -->
					<div v-if="step == 2">

						<!-- Da processare -->
						<div class="mb-3">
							<h6 class="mb-2">
								Righe da processare ({{ arrayDaProcessare.length }})
								<span class="badge bg-success ms-1">{{ countByOp('I') }} insert</span>
								<span class="badge bg-warning text-dark ms-1">{{ countByOp('U') }} update</span>
								<span class="badge bg-danger ms-1">{{ countByOp('D') }} delete</span>
							</h6>
							<div v-if="arrayDaProcessare.length > 0" class="table-responsive" style="max-height:320px; overflow-y:auto;">
								<table class="table table-sm table-bordered mb-0">
									<thead class="sticky-top bg-secondary text-white">
										<tr>
											<th style="width:40px;">Op</th>
											<th style="width:30px;">Tipo</th>
											<th style="width:60px;">ID Att.</th>
											<th style="width:60px;">ID Voce</th>
											<th>Descrizione</th>
											<th style="width:50px;">Qtà</th>
											<th style="width:50px;">Rip.</th>
											<th style="width:90px;">Prezzo Unit.</th>
											<th style="width:90px;">Totale</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(r, i) in arrayDaProcessare" :key="'dp-'+i" :class="rowClass(r._OP)">
											<td class="text-center fw-bold">{{ r._OP }}</td>
											<td class="text-center text-muted">{{ r._TIPO }}</td>
											<td class="text-center">{{ r._TIPO == 'V' ? r.FK_ID_ATTIVITA : r.ID_ATTIVITA }}</td>
											<td class="text-center">{{ r._TIPO == 'V' ? r.ID_VOCE : '' }}</td>
											<td>{{ r.TITOLO }}</td>
											<td class="text-end">{{ r._TIPO == 'V' ? r.QUANTITY_PRICE : '' }}</td>
											<td class="text-end">{{ r._TIPO == 'V' ? r.DAYS_PRICE : '' }}</td>
											<td class="text-end">{{ r._TIPO == 'V' ? formatNum(r.UNIT_PRICE) : '' }}</td>
											<td class="text-end">{{ r._TIPO == 'V' ? formatNum(r.TOTAL_PRICE) : '' }}</td>
										</tr>
									</tbody>
								</table>
							</div>
							<div v-else class="text-muted small">Nessuna riga da processare.</div>
						</div>

						<!-- Conflitti -->
						<div v-if="arrayConflitti.length > 0" class="mb-3">
							<h6 class="mb-2 text-danger">
								<i class="ph-warning me-1"></i>Conflitti ({{ arrayConflitti.length }}) — il DB è più recente dell'Excel
							</h6>
							<p class="text-muted small">Per ogni conflitto scegli quale versione mantenere. Default: Mantieni DB.</p>
							<div class="table-responsive" style="max-height:300px; overflow-y:auto;">
								<table class="table table-sm table-bordered mb-0">
									<thead class="sticky-top bg-danger text-white">
										<tr>
											<th style="width:30px;">Tipo</th>
											<th>Descrizione DB</th>
											<th>Descrizione Excel</th>
											<th style="width:80px;">Totale DB</th>
											<th style="width:80px;">Totale Excel</th>
											<th style="width:120px;">Scelta</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(c, i) in arrayConflitti" :key="'cf-'+i">
											<td class="text-center text-muted">{{ c._TIPO }}</td>
											<td>{{ c._DB_VALUES && c._DB_VALUES.TITOLO }}</td>
											<td>{{ c.TITOLO }}</td>
											<td class="text-end">{{ c._TIPO == 'V' && c._DB_VALUES ? formatNum(c._DB_VALUES.TOTAL_PRICE) : '' }}</td>
											<td class="text-end">{{ c._TIPO == 'V' ? formatNum(c.TOTAL_PRICE) : '' }}</td>
											<td class="text-center">
												<div class="btn-group btn-group-sm">
													<button class="btn" :class="risoluzione[i] == 'DB' ? 'btn-secondary' : 'btn-outline-secondary'" @click="risoluzione[i] = 'DB'">DB</button>
													<button class="btn" :class="risoluzione[i] == 'EXCEL' ? 'btn-warning' : 'btn-outline-warning'" @click="risoluzione[i] = 'EXCEL'">Excel</button>
												</div>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>

						<div v-if="arrayDaProcessare.length == 0 && arrayConflitti.length == 0" class="alert alert-info">
							Nessuna modifica rilevata nel file.
						</div>

						<div v-if="message" class="alert" :class="messageClass">{{ message }}</div>

						<div class="modal-footer px-0 pb-0">
							<button type="button" class="btn btn-secondary" @click="step = 1">Indietro</button>
							<button type="button" class="btn btn-primary" @click="step = 3"
								:disabled="arrayDaProcessare.length == 0 && conflittiRisoltiExcel.length == 0">
								Avanti
							</button>
						</div>
					</div>

					<!-- STEP 3: Conferma -->
					<div v-if="step == 3">
						<div class="alert alert-success">
							<i class="ph-check-circle me-2"></i>
							Conferma importazione:
							<strong>{{ countByOp('I') }} insert</strong>,
							<strong>{{ countByOp('U') }} update</strong>,
							<strong>{{ countByOp('D') }} delete</strong>
							<span v-if="conflittiRisoltiExcel.length > 0">
								+ <strong>{{ conflittiRisoltiExcel.length }} conflitti risolti con Excel</strong>
							</span>
						</div>
						<p class="text-muted small">Le righe con conflitto impostate su "Mantieni DB" verranno ignorate.</p>

						<div v-if="message" class="alert" :class="messageClass">{{ message }}</div>

						<div class="modal-footer px-0 pb-0">
							<button type="button" class="btn btn-secondary" @click="step = 2" :disabled="loading">Indietro</button>
							<button type="button" class="btn btn-primary" @click="confirmImport()" :disabled="loading">
								<span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
								Importa
							</button>
						</div>
					</div>

				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'ImportAttivitaExcel',
	props: {
		idProgetto:  { type: [Number, String], required: true },
		idRevisione: { type: [Number, String], required: true },
	},
	emits: ['imported', 'close'],
	data() {
		return {
			step:              1,
			selectedFile:      null,
			arrayDaProcessare: [],
			arrayConflitti:    [],
			risoluzione:       [],
			message:           '',
			messageClass:      '',
			loading:           false,
		};
	},
	computed: {
		conflittiRisoltiExcel() {
			return this.arrayConflitti.filter((_, i) => this.risoluzione[i] == 'EXCEL');
		},
	},
	methods: {
		onFileSelect(e) {
			this.selectedFile = e.target.files[0] || null;
			this.message = '';
		},

		scaricaTemplate() {
			utils.downloadExcel('theb/attivita-voci/export', {
				FK_ID_PROGETTO:  this.idProgetto,
				FK_ID_REVISIONE: this.idRevisione,
			}, 'AttivitaVoci_template.xlsx');
		},

		parseExcel() {
			if (!this.selectedFile) return;
			this.loading = true;
			this.message = '';

			const formData = new FormData();
			formData.append('file1', this.selectedFile);
			formData.append('args', JSON.stringify({
				modalita:        'THEB_uploadAttivitaVociExcel',
				FK_ID_PROGETTO:  this.idProgetto,
				FK_ID_REVISIONE: this.idRevisione,
			}));

			utils.uploadAjax('core/basic/upload', formData, (res) => {
				this.loading = false;
				if (res.esito == 0) {
					this.arrayDaProcessare = res.data.da_processare || [];
					this.arrayConflitti    = res.data.conflitti     || [];
					this.risoluzione       = this.arrayConflitti.map(() => 'DB');
					this.step = 2;
				} else {
					this.message      = res.messaggio || 'Errore nel parsing del file';
					this.messageClass = 'alert-danger';
				}
			});
		},

		confirmImport() {
			this.loading = true;
			this.message = '';

			const payload = {
				FK_ID_PROGETTO:    this.idProgetto,
				FK_ID_REVISIONE:   this.idRevisione,
				da_processare:     this.arrayDaProcessare,
				conflitti_risolti: this.conflittiRisoltiExcel,
			};

			utils.ajax('theb/attivita-voci/import-bulk', payload, (res) => {
				this.loading = false;
				if (res.esito == 0) {
					utils.alert.success(`Importazione completata: ${res.data.attivita_count} attività, ${res.data.voci_count} voci`);
					this.$emit('imported');
					this.close();
				} else {
					this.message      = res.messaggio || 'Errore durante l\'importazione';
					this.messageClass = 'alert-danger';
				}
			});
		},

		countByOp(op) {
			return this.arrayDaProcessare.filter(r => r._OP == op).length;
		},

		rowClass(op) {
			if (op == 'I') return 'table-success';
			if (op == 'D') return 'table-danger';
			return '';
		},

		formatNum(val) {
			const n = parseFloat(val);
			if (isNaN(n)) return '';
			return '€ ' + n.toFixed(2).replace('.', ',');
		},

		close() {
			this.$emit('close');
		},
	},
};
</script>
